/**
 * Config Store - Manages device configuration
 */

import { defineStore } from 'pinia';
import { ConfigManager } from '../lib/config.js';
import { parseConfigBytes, validateChecksum, validateConfigMatch } from '../lib/utils.js';
import { useDeviceStore } from './device.js';

export const useConfigStore = defineStore('config', {
  state: () => ({
    // Configuration data
    configBytes: null,          // Uint8Array(57)
    parsedConfig: null,         // Parsed config object
    configName: 'Untitled',
    
    // Available presets
    presets: [],
    selectedPreset: null,
    
    // Edit state
    isDirty: false,
    lastLoaded: null,
    lastSaved: null,

    // Write validation state
    lastWriteValidation: null,
    
    // Config manager
    manager: new ConfigManager()
  }),

  getters: {
    hasConfig: (state) => state.configBytes !== null,
    
    currentValues: (state) => {
      if (!state.parsedConfig) return null;
      
      return {
        rollWarning: state.parsedConfig.rollWarning,
        pitchWarning: state.parsedConfig.pitchWarning,
        alarmAngle: state.parsedConfig.alarmAngle,
        buzzerEnabled: state.parsedConfig.buzzerEnabled,
        xAxisForward: state.parsedConfig.xAxisForward
      };
    },
    
    isValidConfig: (state) => {
      if (!state.parsedConfig) return false;
      
      const { rollWarning, pitchWarning, alarmAngle } = state.parsedConfig;
      
      // Validate ranges
      if (rollWarning < 0 || rollWarning > 90) return false;
      if (pitchWarning < 0 || pitchWarning > 90) return false;
      if (alarmAngle < 0 || alarmAngle > 180) return false;
      
      // Alarm angle must be >= warning angles
      if (alarmAngle < Math.max(rollWarning, pitchWarning)) return false;
      
      return true;
    }
  },

  actions: {
    /**
     * Load preset list from server
     */
    async loadPresets() {
      const deviceStore = useDeviceStore();
      
      try {
        deviceStore.addLog('info', 'Loading preset list...');
        this.presets = await this.manager.listConfigs();
        deviceStore.addLog('success', `Loaded ${this.presets.length} presets`);
      } catch (err) {
        deviceStore.addLog('error', `Failed to load presets: ${err.message}`);
        throw err;
      }
    },

    /**
     * Load a preset configuration
     */
    async loadPreset(filename) {
      const deviceStore = useDeviceStore();
      
      try {
        deviceStore.addLog('info', `Loading preset: ${filename}`);
        
        const { name, bytes, config } = await this.manager.loadConfig(filename);
        
        this.configBytes = bytes;
        this.parsedConfig = config;
        this.configName = name;
        this.selectedPreset = filename;
        this.isDirty = false;
        this.lastLoaded = new Date();
        
        deviceStore.addLog('success', `Loaded preset: ${name}`);
        deviceStore.addLog('info', `  Roll: ${config.rollWarning}°, Pitch: ${config.pitchWarning}°, Alarm: ${config.alarmAngle}°`);
      } catch (err) {
        deviceStore.addLog('error', `Failed to load preset: ${err.message}`);
        throw err;
      }
    },

    /**
     * Create new default configuration
     */
    createDefault() {
      const deviceStore = useDeviceStore();
      
      try {
        // Pass firmware version to create version-specific config
        const version = deviceStore.protocol?.firmwareVersion || deviceStore.firmwareVersion;
        const bytes = this.manager.createDefaultConfig(version);
        
        this.configBytes = bytes;
        this.parsedConfig = parseConfigBytes(bytes);
        this.configName = 'Default Configuration';
        this.selectedPreset = null;
        this.isDirty = false;
        this.lastLoaded = new Date();
        
        deviceStore.addLog('info', `Created default configuration for v${version || '1.55'} (${bytes.length} bytes)`);
      } catch (err) {
        deviceStore.addLog('error', `Failed to create default config: ${err.message}`);
        throw err;
      }
    },

    /**
     * Read configuration from device
     */
    async readFromDevice() {
      const deviceStore = useDeviceStore();
      
      if (!deviceStore.protocol || !deviceStore.isConnected) {
        throw new Error('Not connected to device');
      }

      if (deviceStore.waitingForWakeup) {
        throw new Error('Device appears asleep. Press the device button or turn key on, then wait for wake-up.');
      }
      
      try {
        deviceStore.addLog('info', 'Reading config from device...');
        
        const bytes = await deviceStore.protocol.readConfig();
        
        this.configBytes = bytes;
        this.parsedConfig = parseConfigBytes(bytes);
        this.configName = 'From Device';
        this.selectedPreset = null;
        this.isDirty = false;
        this.lastLoaded = new Date();

        // Keep device store orientation source-of-truth synced with actual device config.
        deviceStore.setDeviceXAxisForward(this.parsedConfig.xAxisForward);
        
        deviceStore.addLog('success', 'Config read from device');
        deviceStore.addLog('info', `  Roll: ${this.parsedConfig.rollWarning}°, Pitch: ${this.parsedConfig.pitchWarning}°, Alarm: ${this.parsedConfig.alarmAngle}°`);
      } catch (err) {
        deviceStore.addLog('error', `Failed to read from device: ${err.message}`);
        throw err;
      }
    },

    /**
     * Write configuration to device
     */
    async writeToDevice() {
      const deviceStore = useDeviceStore();
      
      if (!deviceStore.protocol || !deviceStore.isConnected) {
        throw new Error('Not connected to device');
      }

      if (deviceStore.waitingForWakeup) {
        throw new Error('Device appears asleep. Press the device button or turn key on before writing config.');
      }
      
      if (!this.configBytes) {
        throw new Error('No configuration loaded');
      }
      
      if (!this.isValidConfig) {
        throw new Error('Invalid configuration');
      }
      
      try {
        deviceStore.addLog('info', 'Writing config to device...');

        // Snapshot bytes being written to avoid mutation/race while writing
        const writtenBytes = new Uint8Array(this.configBytes);
        const version = deviceStore.protocol?.firmwareVersion || deviceStore.firmwareVersion;

        // Pre-flight checksum validation on outgoing payload
        const outgoingChecksum = validateChecksum(writtenBytes, version);
        if (!outgoingChecksum.valid) {
          throw new Error(`Outgoing config checksum invalid: computed ${outgoingChecksum.computed}, provided ${outgoingChecksum.provided}`);
        }
         
        // Use writeConfigSafe which automatically pauses/resumes sampling
        // and handles the complete V + P command sequence
        await deviceStore.protocol.writeConfigSafe(writtenBytes);

        // Audit write by reading config back and comparing byte-for-byte
        deviceStore.addLog('info', 'Auditing write: reading config back from device...');
        const readBackBytes = await deviceStore.protocol.readConfig();

        const readBackChecksum = validateChecksum(readBackBytes, version);
        const comparison = validateConfigMatch(writtenBytes, readBackBytes, version);

        // Re-parse from readback so source-of-truth reflects actual device state,
        // not just pending UI edits.
        this.configBytes = readBackBytes;
        this.parsedConfig = parseConfigBytes(readBackBytes);
        deviceStore.setDeviceXAxisForward(this.parsedConfig.xAxisForward);

        this.lastWriteValidation = {
          timestamp: new Date(),
          version,
          expectedLength: writtenBytes.length,
          actualLength: readBackBytes.length,
          valid: comparison.valid,
          checksum: {
            expectedValid: comparison.checksum.expectedValid,
            actualValid: comparison.checksum.actualValid,
            readBackComputed: readBackChecksum.computed,
            readBackProvided: readBackChecksum.provided
          },
          mismatchCount: comparison.mismatchCount,
          mismatches: comparison.mismatches
        };

        if (!comparison.valid) {
          const mismatchPreview = comparison.mismatches
            .slice(0, 5)
            .map((m) => `#${m.index}: sent=${m.expected}, read=${m.actual}`)
            .join(', ');

          throw new Error(
            `Write verification failed: ${comparison.mismatchCount} byte mismatch(es)` +
            `${comparison.mismatchCount > 0 ? ` (${mismatchPreview}${comparison.mismatchCount > 5 ? ', ...' : ''})` : ''}`
          );
        }
         
        this.isDirty = false;
        this.lastSaved = new Date();
         
        deviceStore.addLog('success', 'Config written to device, programmed to EEPROM, and validated by readback audit');
      } catch (err) {
        deviceStore.addLog('error', `Failed to write to device: ${err.message}`);
        throw err;
      }
    },

    /**
     * Update configuration values
     */
    updateValues(changes) {
      const deviceStore = useDeviceStore();
      
      if (!this.configBytes) {
        deviceStore.addLog('warning', 'No config loaded, creating default');
        this.createDefault();
      }
      
      try {
        // Pass firmware version to ensure version-specific checksum calculation
        const version = deviceStore.protocol?.firmwareVersion || deviceStore.firmwareVersion;
        
        // Update config bytes
        this.configBytes = this.manager.updateConfig(this.configBytes, changes, version);
        
        // Reparse
        this.parsedConfig = parseConfigBytes(this.configBytes);
        
        // Mark as dirty
        this.isDirty = true;
        this.selectedPreset = null; // No longer matches a preset
        
        deviceStore.addLog('info', 'Configuration updated');
      } catch (err) {
        deviceStore.addLog('error', `Failed to update config: ${err.message}`);
        throw err;
      }
    },

    /**
     * Reset to last loaded state
     */
    reset() {
      const deviceStore = useDeviceStore();
      
      if (this.selectedPreset) {
        this.loadPreset(this.selectedPreset);
      } else {
        deviceStore.addLog('warning', 'No preset to reset to');
      }
    }
  }
});
